"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import PricingData from "../../data/pricing.json";

const Pricing = ({ parentClass, hasSession, subscriptionFlowStatus }) => {
  const router = useRouter();
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;

  const { data: session } = useSession();
  const userEmail = session?.user?.email;

  // Tabs (Mensual / Anual)
  const initialTabId = useMemo(() => {
    const selected = PricingData?.pricing?.find((p) => p.isSelect)?.priceId;
    return selected || PricingData?.pricing?.[0]?.priceId || "";
  }, []);

  const [activeTabId, setActiveTabId] = useState(initialTabId);

  useEffect(() => {
    if (!activeTabId && initialTabId) setActiveTabId(initialTabId);
  }, [activeTabId, initialTabId]);

  const activeSection = useMemo(() => {
    return PricingData?.pricing?.find((p) => p.priceId === activeTabId) || null;
  }, [activeTabId]);

  const visiblePlans = activeSection?.priceBody || [];

  // Helpers: ciclo y priceId (Paddle)
  const getBillingCycleKey = (sectionPriceType) => {
    const t = (sectionPriceType || "").toLowerCase();
    if (t.includes("anual") || t.includes("year")) return "yearly";
    return "monthly";
  };

  const getUnlimitedPriceId = (billingCycleKey) => {
    if (billingCycleKey === "yearly") {
      return process.env.NEXT_PUBLIC_PAYMENT_GATEWAY_YEARLY_UNLIMITED_PLAN;
    }
    return process.env.NEXT_PUBLIC_PAYMENT_GATEWAY_MONTHLY_UNLIMITED_PLAN;
  };

  // Carga Paddle.js
  const loadPaddle = () => {
    return new Promise((resolve, reject) => {
      if (typeof window === "undefined") return resolve();
      if (window.Paddle) return resolve();

      const script = document.createElement("script");
      script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
      script.async = true;

      script.onload = () => {
        try {
          // cambia a "production" cuando lances
          if (process.env.NEXT_PUBLIC_ENV === "dev")
            window.Paddle.Environment.set("sandbox");

          window.Paddle.Initialize({
            token: process.env.NEXT_PUBLIC_PAYMENT_GATEWAY_PUBLIC_KEY,
            eventCallback: async (event) => {
              try {
                if (event?.name !== "checkout.completed") return;

                const txnId = event?.data?.transaction_id;
                if (!txnId) {
                  router.push(
                    `/error?message=${encodeURIComponent(
                      "No se pudo obtener el ID de transacción."
                    )}`
                  );
                  return;
                }

                const billingCycleKey = getBillingCycleKey(
                  activeSection?.priceType
                );

                const res = await fetch(`${apiUrl}/user/create-subscription`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${session?.token}`,
                  },
                  body: JSON.stringify({
                    transaction_id: txnId,
                    price_id: getUnlimitedPriceId(billingCycleKey),
                  }),
                });

                const result = await res.json().catch(() => ({}));

                if (!res.ok) {
                  router.push(
                    `/error?message=${encodeURIComponent(
                      result.detail ||
                        result.message ||
                        "Error al registrar la suscripción."
                    )}`
                  );
                  return;
                }

                const plan_name = result.plan_name || "Plan Ilimitado";
                const next_billing_date =
                  result.next_billing_date || "Por confirmar";

                router.push(
                  `/cuenta/suscripcion/nueva/exito?plan=${encodeURIComponent(
                    plan_name
                  )}&proxima_renovacion=${encodeURIComponent(next_billing_date)}`
                );
              } catch (err) {
                console.error("Error en eventCallback:", err);
                router.push(
                  `/error?message=${encodeURIComponent(
                    "Ocurrió un error procesando el pago. Intenta nuevamente."
                  )}`
                );
              }
            },
          });

          resolve();
        } catch (e) {
          reject(e);
        }
      };

      script.onerror = () => reject(new Error("No se pudo cargar Paddle.js"));
      document.body.appendChild(script);
    });
  };

  // Checkout (general)
  const openUnlimitedCheckout = async () => {
    try {
      if (!session?.token) {
        router.push(
          `/error?message=${encodeURIComponent(
            "No hay sesión activa. Por favor, inicia sesión."
          )}`
        );
        return;
      }

      await loadPaddle();

      const billingCycleKey = getBillingCycleKey(activeSection?.priceType);
      const paddlePriceId = getUnlimitedPriceId(billingCycleKey);

      if (!paddlePriceId) {
        router.push(
          `/error?message=${encodeURIComponent(
            "No se encontró el priceId del plan seleccionado."
          )}`
        );
        return;
      }

      window.Paddle.Checkout.open({
        items: [{ priceId: paddlePriceId, quantity: 1 }],
        locale: "es",
        customer: userEmail ? { email: userEmail } : undefined,
        customData: {
          userEmail: userEmail || null,
          billing: billingCycleKey,
        },
        settings: {
          showAddDiscounts: false,
          showAddTaxId: false,
        },
      });
    } catch (err) {
      console.error("Error abriendo Paddle checkout:", err);
      router.push(
        `/error?message=${encodeURIComponent(
          "Error abriendo el checkout. Intenta nuevamente."
        )}`
      );
    }
  };

  // Show More
  const [sectionStates, setSectionStates] = useState({
    Gratuito: true,
    Ilimitado: true,
    Institucional: true,
  });

  const toggleSection = (subTitle) => {
    setSectionStates((prev) => ({ ...prev, [subTitle]: !prev[subTitle] }));
  };

  // Botones
  const getButtonProps = (planTitle) => {
    if (!hasSession) {
      if (planTitle === "Gratuito" || planTitle === "Ilimitado") {
        return { text: "Empezar", href: "/registrarse", disabled: false };
      }
      if (planTitle === "Institucional") {
        return { text: "Contáctanos", href: "/contacto", disabled: false };
      }
    }

    switch (planTitle) {
      case "Gratuito":
        return subscriptionFlowStatus === "free_active"
          ? { text: "Plan gratuito activo", href: "#", disabled: true }
          : { text: "Plan gratuito finalizado", href: "#", disabled: true };

      case "Ilimitado":
        if (
          subscriptionFlowStatus === "unlimited_pending" ||
          subscriptionFlowStatus === "unlimited_active" ||
          subscriptionFlowStatus === "unlimited_canceled" ||
          subscriptionFlowStatus === "unlimited_payment_failed" ||
          subscriptionFlowStatus === "unlimited_payment_failed_expired" ||
          subscriptionFlowStatus === "institutional_active"
        ) {
          return { text: "Plan activo", href: "#", disabled: true };
        }

        if (
          subscriptionFlowStatus === "free_active" ||
          subscriptionFlowStatus === "free_expired" ||
          subscriptionFlowStatus === "unlimited_expired" ||
          subscriptionFlowStatus === "institutional_expired"
        ) {
          return {
            text: "Suscribirte",
            href: "#",
            onClick: openUnlimitedCheckout,
            disabled: false,
          };
        }

        return { text: "Sin definir", href: "#", disabled: true };

      case "Institucional":
        return subscriptionFlowStatus === "institutional_active"
          ? { text: "Plan Institucional activo", href: "#", disabled: true }
          : { text: "Contáctanos", href: "/contacto", disabled: false };

      default:
        return { text: "Sin definir", href: "#", disabled: true };
    }
  };

  // Render
  return (
    <div>
      <nav className="aiwave-tab">
        <div
          className="tab-btn-grp nav nav-tabs text-center justify-content-center"
          role="tablist"
        >
          {PricingData?.pricing?.map((section, idx) => {
            const isActive = section.priceId === activeTabId;

            return (
              <button
                key={idx}
                type="button"
                className={`nav-link ${isActive ? "active" : ""}`}
                onClick={() => setActiveTabId(section.priceId)}
              >
                {section.priceType}
                {section.discount ? (
                  <span className="rainbow-badge-card badge-border">
                    -{section.discount}%
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Contenido */}
      <div className="tab-content p-0 bg-transparent border-0 border bg-light">
        {activeSection ? (
          <div className="tab-pane fade active show">
            <div
              className={`row row--15 ${
                visiblePlans.length < 3 ? "justify-content-center" : ""
              }`}
            >
              {visiblePlans.map((plan, i) => {
                const { text, href, onClick, disabled } = getButtonProps(
                  plan.subTitle
                );

                return (
                  <div className={parentClass} key={i}>
                    <div
                      className={`rainbow-pricing style-aiwave ${
                        plan.isSelect ? "active" : ""
                      }`}
                    >
                      <div className="pricing-table-inner">
                        <div className="pricing-top">
                          <div className="pricing-header">
                            <div className="icon">
                              <i className={plan.iconClass}></i>
                            </div>
                            <h4 className={`title color-var-${plan.classNum}`}>
                              {plan.subTitle}
                            </h4>
                            <p className="subtitle">{plan.title}</p>
                            <div className="pricing">
                              <span className="price-text">{plan.price}</span>
                              <span className="text">{plan.priceFor}</span>
                            </div>
                          </div>

                          <div className="pricing-body">
                            <div
                              className={`features-section has-show-more ${
                                !sectionStates[plan.subTitle] ? "active" : ""
                              }`}
                            >
                              <h6>{plan.text}</h6>
                              <ul className="list-style--1 has-show-more-inner-content">
                                {plan.listItem.map((item, idx) => (
                                  <li key={idx}>
                                    <i className="fa-regular fa-circle-check"></i>
                                    {item.text}
                                  </li>
                                ))}
                              </ul>

                              {plan.isShow && (
                                <div
                                  className={`rbt-show-more-btn ${
                                    !sectionStates[plan.subTitle]
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() => toggleSection(plan.subTitle)}
                                >
                                  Ver más
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="pricing-footer">
                          <button
                            className={`btn-default w-100 ${
                              plan.isSelect ? "color-blacked" : "btn-border"
                            }`}
                            onClick={() => {
                              if (onClick) onClick();
                              if (!disabled && href !== "#") router.push(href);
                            }}
                            disabled={disabled}
                          >
                            {text}
                          </button>
                          <p className="bottom-text">{plan.limited}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Pricing;
